import JSZip from 'jszip';
import { supabase } from './supabaseClient';
import { CURRENCY_FA } from './tariffsApi';
import { capacityModel, splitterCounts, dropsMetered } from './capacityModel';
import { reportClientEvent } from './clientLog';

// On-screen route-line colours matched to the real Irancell KMZ
// (metro red / ODC amber-yellow / FAT violet / drop light-violet),
// kept readable over a roadmap basemap.
const ROUTE_COLORS = { metro: '#ef4444', odc: '#eab308', fat: '#8b5cf6', drop: '#a78bfa' };
const ELEMENT_COLORS = {
  // team spec 2026-07-06: 'fat' is the single leaf (renders like the legacy sfat),
  // 'mfat_l2' the MFAT device, a peer of the ODC (cabinet-like). Legacy mfat/sfat KEPT so old
  // saved designs still render.
  // mfat_l2 pink per round-11 (matches the pink KMZ triangle)
  olt: '#16a34a', odc: '#06b6d4', mfat_l2: '#ec4899', fat: '#3b82f6',
  mfat: '#2563eb', sfat: '#3b82f6', hh: '#1e3a8a',
  tconnector: '#f59e0b', hconnector: '#a21caf',
};

const ROUTE_WIDTH = { metro: 3, odc: 2.25, fat: 1.75, drop: 1.75 };

// Legacy layer alias: the ring layer was renamed 'olt_ring' -> 'metro', but the
// rename shipped WITHOUT a data migration, so production still holds persisted
// design_routes rows with layer='olt_ring'. Normalize at every read site that
// keys on route.layer so legacy rows render/export/report as metro.
export function normalizeLayer(layer) {
  return layer === 'olt_ring' ? 'metro' : layer;
}

// round-11: the MFAT feed renders like the FAT line (purple, 4-channel duct), not
// feeder-yellow. Identify it by the engine duct tag or by an mfat_l2 endpoint (the
// endpoint test also fixes designs saved before the tag existed), but only on an 'odc'
// route: round 22 answer 6 draws drops FROM an MFAT's port 1, and a drop stays a drop.
export function routeRenderLayer(route, mfatIds) {
  const norm = normalizeLayer(route.layer);
  const feed = (route.duct || {}).kind === 'mfat-feed'
    || (norm === 'odc' && (mfatIds.has(route.from_ext) || mfatIds.has(route.to_ext)));
  return feed ? 'fat' : norm;
}

export function routeStyle(layer) {
  const l = normalizeLayer(layer);
  return { strokeColor: ROUTE_COLORS[l] || '#64748b', strokeWeight: ROUTE_WIDTH[l] || 2, strokeOpacity: 1 };
}
// A dark halo drawn UNDER each coloured route so thin lines stay legible over a
// busy roadmap basemap (the colour line is painted on top of this).
export function routeHaloStyle(layer) {
  return { strokeColor: '#0b1220', strokeWeight: (ROUTE_WIDTH[normalizeLayer(layer)] || 2) + 1.6, strokeOpacity: 0.3 };
}
export function elementStyle(type) {
  return { fill: ELEMENT_COLORS[type] || '#64748b' };
}

// Projects are created through Express with a published preset version.
// Seed geometry still uses the carrier-gated browser RPCs.
export async function createDesignProject(name, bboxPolygon, mode, params = {}, profileVersionId = null) {
  try {
    const response = await fetch('/api/design/projects', {
      method: 'POST', headers: await authHeaders(true),
      body: JSON.stringify({ name, region: bboxPolygon, mode, params, profile_version_id: profileVersionId }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) return { ...data, error: data?.error || 'ساخت طرح ناموفق بود.', httpStatus: response.status };
    return { ...data, error: null };
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    return { error: 'خطای شبکه.' };
  }
}
export async function createSeed(projectId, type, pointGeometry) {
  const { data, error } = await supabase.rpc('create_design_seed', {
    p_project: projectId, p_type: type, g: pointGeometry,
  });
  return { id: data, error };
}
export async function deleteSeed(id) {
  const { error } = await supabase.rpc('delete_design_seed', { b_id: id });
  return { error };
}
// PostgREST caps any unranged set-returning RPC response at 1000 rows (the same
// gotcha as the table REST reads), and a citywide design has far more elements +
// routes than that. Page through with .range() until a short page comes back.
const RPC_PAGE_SIZE = 1000;
async function rpcAllRows(fn, args) {
  const rows = [];
  let from = 0;
  for (;;) {
    const { data, error } = await supabase.rpc(fn, args).range(from, from + RPC_PAGE_SIZE - 1);
    if (error) throw error;
    const page = data || [];
    rows.push(...page);
    if (page.length < RPC_PAGE_SIZE) return { rows, error: null };
    from += RPC_PAGE_SIZE;
  }
}
export async function loadProject(projectId) {
  try {
    const [els, routes] = await Promise.all([
      rpcAllRows('design_elements_in_project', { p_project: projectId }),
      rpcAllRows('design_routes_in_project', { p_project: projectId }),
    ]);
    return { elements: els.rows, routes: routes.rows, error: null };
  } catch {
    // Reject even for consumers that ignore a returned error field. No partial
    // geometry can become an export or the input to a destructive manual save.
    throw Object.assign(new Error('بارگذاری نتیجهٔ طرح ناموفق بود؛ دوباره تلاش کنید.'), { code: 'DESIGN_GEOMETRY_UNAVAILABLE' });
  }
}

// Build the auth headers for an Express call. json=true adds Content-Type for a
// body-bearing request (mirrors the getSession/Bearer block repeated across this
// module). The Bearer is the operator's Supabase access token.
async function authHeaders(json = false) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = json ? { 'Content-Type': 'application/json' } : {};
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

// --- run (Express proxy) ---------------------------------------------------
// The run is now ASYNC: POST /run inserts a design_jobs row, enqueues it onto
// the arq worker, and returns 202 {job_id} (or {job_id, already_running:true}
// when a run for the project is already in flight). The OLD shape returned
// {result}; every caller now watches the job (watchDesignJob) for progress +
// the final result instead. Keep {error} on a non-ok response.
// seedHints (re-run with edits only): {seedId: {parent_ext, building_ids, demand_ids}},
// each device's previous parent and served buildings / demand points, so an unedited
// re-run is the same design. freshSeedIds: seeds just dragged to a new place; the server
// drops the hints their rows still hold, so a moved device counts as newly added. The
// row moved before its run was sent: until a run of that project is accepted (not
// already_running, not failed or thrown) every later run sends those seeds as fresh too.
const pendingFresh = new Map();         // projectId -> seed ids whose run was not accepted
export async function runDesign(projectId, seedHints, freshSeedIds) {
  const fresh = [...new Set([...(pendingFresh.get(projectId) || []), ...(freshSeedIds || [])])];
  if (fresh.length) pendingFresh.set(projectId, fresh);
  const headers = await authHeaders(true);
  const payload = { projectId };
  if (seedHints) payload.seed_hints = seedHints;
  if (fresh.length) payload.fresh_seed_ids = fresh;
  const resp = await fetch('/api/design/run', { method: 'POST', headers, body: JSON.stringify(payload) });
  if (!resp.ok) {
    const text = await resp.text();
    try { return { error: JSON.parse(text)?.error || 'اجرای طراحی ناموفق بود.' }; }
    catch { return { error: text }; }
  }
  const body = await resp.json();
  if (body.already_running) return { job_id: body.job_id, already_running: true };
  pendingFresh.delete(projectId);
  return { job_id: body.job_id };
}

// Fetch ONE design job by id. Returns {job} ({id,status,stage,progress_total,
// progress_done,result_summary,error,...}) or {error} on a non-ok response.
// A THROWN fetch (offline / DNS / abort) is also converted to {error:'network'}
// so the watcher's interval-driven pollOnce never emits an unhandled rejection
// every tick; the watcher just keeps polling and self-heals when the network
// returns.
export async function getDesignJob(jobId) {
  const headers = await authHeaders(false);
  try {
    const resp = await fetch(`/api/design/jobs/${jobId}`, { headers });
    if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'دریافت وضعیت اجرا ناموفق بود', status: resp.status }; }
    const body = await resp.json();
    return { job: body.job };
  } catch {
    return { error: 'network', network: true, status: 0 };
  }
}

// Fetch the newest ACTIVE (queued|running) job for a project, or {job:null}
// when none is in flight. Used by the resume-on-mount path. A thrown fetch is
// converted to {error} (never propagates).
export async function getActiveDesignJob(projectId) {
  const headers = await authHeaders(false);
  try {
    const resp = await fetch(`/api/design/jobs/active?projectId=${encodeURIComponent(projectId)}`, { headers });
    if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'دریافت اجرای فعال ناموفق بود' }; }
    const body = await resp.json();
    return { job: body.job || null };
  } catch {
    return { error: 'network', network: true };
  }
}

// Cancel a non-terminal job. Returns {ok:true} or {error} (e.g. 409 when the job
// already finished). MARK-ONLY server-side: the worker is fire-and-forget; the
// terminal guard on the internal result callback is what makes the cancel stick.
// A thrown fetch is converted to {error} (never propagates).
export async function cancelDesignJob(jobId) {
  const headers = await authHeaders(false);
  try {
    const resp = await fetch(`/api/design/jobs/${jobId}/cancel`, { method: 'POST', headers });
    if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'لغو اجرا ناموفق بود' }; }
    return { ok: true };
  } catch {
    return { error: 'network', network: true };
  }
}

// Terminal job statuses (no further worker transitions). Mirrors the server enum.
const TERMINAL_JOB_STATUSES = ['complete', 'failed', 'cancelled'];

// Watch a design job: LEAD WITH POLLING (an immediate getDesignJob + a 4s
// interval), with Supabase realtime as the enhancement (the /sb WS-upgrade
// gotcha means realtime may not fire in prod, so the poll is the source of
// truth). Every distinct status/stage/progress update is forwarded to onUpdate
// (deduped so a poll + a realtime event for the same state fire once). On the
// FIRST terminal status the watcher self-stops (clears the interval + removes
// the channel). Returns an idempotent stop().
//
// This is a PLAIN FUNCTION, never a React hook - it must NOT be named use* (a
// use*-named export trips react-hooks/rules-of-hooks and fails the CRA build
// invisibly to jest).
export function watchDesignJob(jobId, onUpdate, opts = {}) {
  const intervalMs = opts.intervalMs || 4000;
  let stopped = false;
  let timer = null;
  let channel = null;
  let lastKey = null;
  let delivering = false;
  let failures = 0;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (timer) { clearInterval(timer); timer = null; }
    if (channel) { try { supabase.removeChannel(channel); } catch { /* best-effort */ } channel = null; }
  };

  // Await async consumers before stopping. A failed geometry read must reach
  // the map lifecycle as a failure, not an empty successful design or a stuck run.
  const emit = async (job) => {
    if (!job || stopped || delivering) return;
    const key = `${job.status}|${job.stage || ''}|${job.progress_done || ''}|${job.progress_total || ''}`;
    if (key === lastKey) return;
    delivering = true;
    try {
      await onUpdate(job);
      lastKey = key;
      if (TERMINAL_JOB_STATUSES.includes(job.status)) stop();
    } catch (error) {
      if (!stopped && error?.code === 'DESIGN_GEOMETRY_UNAVAILABLE') {
        try {
          await onUpdate({ ...job, status: 'failed', error: error.message });
          stop();
        } catch { /* Retry delivery on the next poll if the consumer fails. */ }
      }
    } finally { delivering = false; }
  };

  const pollOnce = async () => {
    if (stopped) return;
    const { job, error, status } = await getDesignJob(jobId);
    // The UI keeps polling quietly; the third failure in a row is worth one report.
    failures = error ? failures + 1 : 0;
    if (failures === 3) reportClientEvent('poll_failed', { design_job_id: jobId, status });
    await emit(job);
  };

  // Realtime is a wake-up only. Poll the ownership-checked HTTP route so raw
  // worker exceptions from payload.new never reach an alert or a lifecycle.
  channel = supabase
    .channel(`design_job_${jobId}`)
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'design_jobs', filter: `id=eq.${jobId}` },
      (payload) => { if (payload && payload.new) pollOnce(); })
    .subscribe();

  // Lead with an immediate poll + the recurring interval.
  pollOnce();
  timer = setInterval(pollOnce, intervalMs);

  return { stop };
}

// On-demand AI analysis for ONE design element (replaces the old bulk narration
// that ran during the design). Returns the Persian explanation; the server caches
// it into the element so a second click is instant and costs no further call.
export async function explainDesignElement(projectId, extId) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const resp = await fetch('/api/design/explain-element', {
    method: 'POST', headers, body: JSON.stringify({ projectId, extId }),
  });
  if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'تحلیل ناموفق بود' }; }
  const body = await resp.json();
  return { explanation: body.explanation };
}

// --- design history (Express proxy) ----------------------------------------
// The caller's past designs, newest first (admins get all). Owner scoping is
// enforced server-side; this is a thin authenticated GET.
export async function listDesignProjects() {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = {};
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const resp = await fetch('/api/design/projects', { headers });
  if (!resp.ok) {
    const j = await resp.json().catch(() => ({}));
    return { error: j.error || 'دریافت تاریخچه ناموفق بود' };
  }
  const body = await resp.json();
  return { projects: body.projects || [] };
}

// Save a frozen version snapshot of the current (working) design. Returns the
// new version number; the working project itself is untouched.
export async function snapshotDesignVersion(projectId) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const resp = await fetch('/api/design/snapshot', { method: 'POST', headers, body: JSON.stringify({ projectId }) });
  if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'ذخیرهٔ نسخه ناموفق بود' }; }
  const body = await resp.json();
  return { id: body.id, version: body.version };
}

// Persist an edited design AS-IS (no engine re-run); marks it manual_edited so the
// stale cabling/feasibility is flagged as needing recompute.
export async function saveDesignManual(projectId, result) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const resp = await fetch('/api/design/save-manual', {
    method: 'POST', headers,
    body: JSON.stringify({
      projectId, elements: result.elements || [], routes: result.routes || [], summary: result.summary || {},
    }),
  });
  if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'ذخیرهٔ ویرایش ناموفق بود' }; }
  return { ok: true };
}

// All versions in a design's group (working project first, then snapshots).
export async function listDesignVersions(projectId) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = {};
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const resp = await fetch(`/api/design/projects/${projectId}/versions`, { headers });
  if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'دریافت نسخه‌ها ناموفق بود' }; }
  const body = await resp.json();
  return { versions: body.versions || [] };
}

// AI narrative report (Express proxy -> design_report surface). Returns the
// Persian report text, grounded on the design's stored summary + rule_audit.
export async function buildSmartReport(projectId) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  const resp = await fetch('/api/design/report', { method: 'POST', headers, body: JSON.stringify({ projectId }) });
  if (!resp.ok) { const j = await resp.json().catch(() => ({})); return { error: j.error || 'گزارش ناموفق بود' }; }
  const body = await resp.json();
  return { report: body.report };
}

// --- building unit count ---------------------------------------------------
export async function setBuildingUnits(buildingId, units, floors = null) {
  const { error } = await supabase.rpc('set_building_units', {
    b_id: buildingId, p_units: units, p_floors: floors,
  });
  return { error };
}

// move a seed (drag-to-reposition); g = GeoJSON Point
export async function updateSeed(id, pointGeometry) {
  const { error } = await supabase.rpc('update_design_seed', { b_id: id, g: pointGeometry });
  return { error };
}

// --- KMZ export: Irancell folder structure (Point/Line by type) -------------
// KML colour = aabbggrr. Values + icon hrefs extracted from the real Irancell
// Dolatabad KMZ (authoritative): Metro RED / ODC line YELLOW / FAT line PURPLE.
const LINE_KML = { metro: 'ff0000ff', odc: 'ff00ffff', fat: 'ff7f00aa', drop: 'ff7f00aa' };
const LINE_WIDTH = { metro: 5, odc: 3, fat: 3, drop: 3 };
// Per-type point icons: shape href + aabbggrr colour + scale (Irancell exact).
const PT_STYLE = {
  olt: { href: 'http://maps.google.com/mapfiles/kml/shapes/grn-pushpin.png', color: 'ffffffff', scale: 1.1 },
  odc: { href: 'http://maps.google.com/mapfiles/kml/shapes/homegardenbusiness.png', color: 'ffffff00', scale: 1.0 },
  fat: { href: 'http://maps.google.com/mapfiles/kml/shapes/triangle.png', color: 'ffff0000', scale: 0.9 },
  hh: { href: 'http://maps.google.com/mapfiles/kml/shapes/square.png', color: 'ff7f0000', scale: 0.8 },
  // round-11 team ask: MFAT symbol = a TRIANGLE like the FAT but PINK.
  mfat_l2: { href: 'http://maps.google.com/mapfiles/kml/shapes/triangle.png', color: 'ffb469ff', scale: 1.0 },
  // T-connector = orange circle (single inline tap); H-connector = magenta donut
  // (a shared branch hub) - distinct shape AND colour so a reviewer can tell them
  // apart in Google Earth (round-6 design-team ask: "is this a T or an H?").
  tc: { href: 'http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png', color: 'ff00a5ff', scale: 0.9 },
  hc: { href: 'http://maps.google.com/mapfiles/kml/shapes/donut.png', color: 'ffcf1ca2', scale: 1.0 },
};
// POP coverage circle outlines: OLT red / ODC yellow, width 2 (transparent fill).
const AREA_KML = { olt: '330000ff', odc: '3300ffff' };
const AREA_LINE = { olt: 'ff0000ff', odc: 'ff00ffff' };
function kmlEsc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

function circleRing(lng, lat, radiusM, n = 32) {
  const dLat = radiusM / 111320;
  const dLng = radiusM / (111320 * Math.cos(lat * Math.PI / 180));
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const a = (2 * Math.PI * i) / n;
    pts.push(`${lng + dLng * Math.cos(a)},${lat + dLat * Math.sin(a)},0`);
  }
  return pts.join(' ');
}

// Round-11 team naming (answers 2026-07-10), replacing the round-6 FIX-10 scheme:
//   OLT   {CITY}-OLTNN
//   ODC   {CITY}-OLTNN-POPKK-ODCKK          (KK per parent OLT; POP number == ODC number)
//   MFAT  {CITY}-OLTNN-POP0-MFATNN          (the OLT's direct pool is the literal POP0)
//   FAT   {parent ODC name}-SFATKK, or {CITY}-OLTNN-POP0-SFATKK under the MFAT pool
//         (FATs under an MFAT carry NO MFAT segment - team example E1707)
//   HH/TC/HC  {CITY}-{HH|TC|HC}-NN
// CITY is DERIVED from the city dataset (team answer 2): nearest known imported
// city to the design's own centroid; falls back to the site token recovered from
// the equipment ext_ids, else 'FTTH'. Pure DISPLAY: ext_id stays the load-bearing
// identity for parent_ext / route from_ext-to_ext linkage.
const pad2 = (n) => String(n).padStart(2, '0');
// [code, lat, lng] of the imported city datasets (Supabase). Extend when a new
// city dataset is imported. Match radius ~0.35 deg (~35 km).
const CITY_DATASETS = [
  ['DLT', 32.80, 51.69],   // Dowlatabad (Isfahan) - municipal CAD
  ['QOM', 34.64, 50.88],   // Qom - karbari parcels
  ['MRV', 35.52, 46.18],   // Marivan - OSM
  ['BHR', 32.48, 51.77],   // Baharestan (Isfahan) - annotation ground truth
];
function cityCodeFor(els) {
  let sLat = 0; let sLng = 0; let n = 0;
  for (const e of els) {
    const c = e.point || (e.geometry && e.geometry.coordinates);
    if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) { sLng += c[0]; sLat += c[1]; n += 1; }
  }
  if (!n) return null;
  const lat = sLat / n; const lng = sLng / n;
  let best = null; let bestD = 0.35;
  for (const [code, cLat, cLng] of CITY_DATASETS) {
    const d = Math.hypot(lat - cLat, lng - cLng);
    if (d < bestD) { bestD = d; best = code; }
  }
  return best;
}
export function designDisplayNames(elements) {
  const els = elements || [];
  let site = 'FTTH';
  for (const e of els) {
    const m = String(e.ext_id || '').match(/^(.+?)-(?:MTN-A|TC|HC|HH)-\d+$/i);
    if (m) { site = m[1]; break; }
  }
  const city = cityCodeFor(els) || site;
  const sortByExt = (t) => els.filter((e) => e.type === t)
    .slice().sort((a, b) => String(a.ext_id).localeCompare(String(b.ext_id)));
  const names = {};
  const oltTok = {};
  sortByExt('olt').forEach((e, i) => {
    oltTok[e.ext_id] = `OLT${pad2(i + 1)}`;
    names[e.ext_id] = `${city}-OLT${pad2(i + 1)}`;
  });
  const oltOf = (e) => oltTok[e.parent_ext] || 'OLT00';
  // fatPrefix: what a child FAT's name starts with (full ODC name / the MFAT pool's POP0)
  const fatPrefix = {};
  const odcSeq = {};
  sortByExt('odc').forEach((e) => {
    const ot = oltOf(e);
    const k = (odcSeq[ot] = (odcSeq[ot] || 0) + 1);
    const nm = `${city}-${ot}-POP${pad2(k)}-ODC${pad2(k)}`;
    names[e.ext_id] = nm; fatPrefix[e.ext_id] = nm;
  });
  const mfatSeq = {};
  sortByExt('mfat_l2').forEach((e) => {
    const ot = oltOf(e);
    const k = (mfatSeq[ot] = (mfatSeq[ot] || 0) + 1);
    names[e.ext_id] = `${city}-${ot}-POP0-MFAT${pad2(k)}`;
    fatPrefix[e.ext_id] = `${city}-${ot}-POP0`;
  });
  const fatN = {};
  els.filter((e) => e.type === 'mfat' || e.type === 'sfat' || e.type === 'fat')
    .slice().sort((a, b) => String(a.ext_id).localeCompare(String(b.ext_id)))
    .forEach((e) => {
      const pref = fatPrefix[e.parent_ext] || `${city}-OLT00-POP00`;
      fatN[pref] = (fatN[pref] || 0) + 1;
      names[e.ext_id] = `${pref}-SFAT${pad2(fatN[pref])}`;
    });
  [['hh', 'HH'], ['tconnector', 'TC'], ['hconnector', 'HC']].forEach(([t, tag]) =>
    sortByExt(t).forEach((e, i) => { names[e.ext_id] = `${city}-${tag}-${pad2(i + 1)}`; }));
  return names;
}

// round-7 ISSUE-5: the engine routes the lower layers (ODC, FAT) onto the SAME
// street centerline as the metro backbone but with different vertices, so its
// exact-edge dedup leaves them drawn ON TOP of the metro in the KMZ - the team:
// «خط مترو و ODC و FAT نباید روی هم ترسیم شده باشند» (~4.4 km of FAT/ODC stacked on
// metro in the tested plan). Clip each lower layer against the cumulative higher-
// priority geometry so each metre of trench is drawn ONCE, at its highest layer.
// Pure-cosmetic: only the exported line arrays change; engine routes/costs/BOM are
// untouched (the cable still physically rides the shared trench - that is the
// reuse-discount, here we just stop re-INKING it).
const EXPORT_CLIP_M = 1.75;        // ~ half the rendered metro line width, in metres

function distPointSeg(p, a, b) {
  const vx = b[0] - a[0], vy = b[1] - a[1];
  const len2 = vx * vx + vy * vy;
  let t = len2 ? ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p[0] - (a[0] + t * vx), p[1] - (a[1] + t * vy));
}

// Mutates rtByLayer.odc / rtByLayer.fat: drops the sub-segments that run under a
// higher-priority layer (metro > odc > fat). A line wholly under a higher layer
// vanishes (it was redundant ink); a partial one splits into its unique run(s).
export function clipLayersForExport(rtByLayer) {
  const all = [...rtByLayer.metro, ...rtByLayer.odc, ...rtByLayer.fat];
  if (!all.length) return rtByLayer;
  const lat0 = all[0][0][1];
  const mLng = 111320 * Math.cos((lat0 * Math.PI) / 180), mLat = 110540;
  const toM = ([lng, lat]) => [lng * mLng, lat * mLat];
  const covered = [];
  const addCovered = (lines) => lines.forEach((ln) => {
    for (let i = 0; i < ln.length - 1; i++) covered.push([toM(ln[i]), toM(ln[i + 1])]);
  });
  // a lower segment is "covered" only if 3 sample points (¼, ½, ¾ along it) are ALL
  // within the band of a higher layer - conservative, so a short segment that merely
  // grazes a higher line at its midpoint is NOT erased (round-7 review false-positive).
  const segCovered = (p, q) => [0.25, 0.5, 0.75].every((t) => {
    const s = [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
    return covered.some((c) => distPointSeg(s, c[0], c[1]) <= EXPORT_CLIP_M);
  });
  const clipLines = (lines) => lines.flatMap((ln) => {
    const runs = []; let run = [];
    for (let i = 0; i < ln.length - 1; i++) {
      if (segCovered(toM(ln[i]), toM(ln[i + 1]))) { if (run.length >= 2) runs.push(run); run = []; }
      else { if (!run.length) run.push(ln[i]); run.push(ln[i + 1]); }
    }
    if (run.length >= 2) runs.push(run);
    return runs;
  });
  addCovered(rtByLayer.metro);                 // metro drawn whole (highest priority)
  rtByLayer.odc = clipLines(rtByLayer.odc);    // ODC keeps only the part NOT under metro
  addCovered(rtByLayer.odc);
  rtByLayer.fat = clipLines(rtByLayer.fat);    // FAT keeps only the part under neither
  return rtByLayer;
}

// coverageM defaults to the radius the design actually ran at (500 m engine default),
// so the KMZ coverage circles never disagree with the run.
export function buildDesignKml(result, city = 'FTTH', brand = {},
  coverageM = ((result.summary || {}).validator || {}).serving_radius_m || 500) {
  const dispName = designDisplayNames(result.elements);
  const elByType = { olt: [], odc: [], mfat_l2: [], fat: [], hh: [], tc: [], hc: [] };
  (result.elements || []).forEach((e) => {
    const c = e.point || (e.geometry && e.geometry.coordinates);
    if (!c) return;
    let t = e.type;
    // team spec 2026-07-06: legacy per-FAT mfat/sfat AND the new 'fat' leaf all collapse
    // to one FAT placemark; the level-2 'mfat_l2' device keeps its own cabinet placemark.
    if (t === 'mfat' || t === 'sfat' || t === 'fat') t = 'fat';
    else if (t === 'tconnector') t = 'tc';
    else if (t === 'hconnector') t = 'hc';
    if (elByType[t]) elByType[t].push({ name: dispName[e.ext_id] || e.ext_id || t, c });
  });
  const rtByLayer = { metro: [], odc: [], fat: [] };
  const lenByLayer = { metro: 0, odc: 0, fat: 0 };
  // The MFAT feed renders like the FAT line (routeRenderLayer); drops fold into it too.
  const mfatIds = new Set((result.elements || []).filter((e) => e.type === 'mfat_l2').map((e) => e.ext_id));
  (result.routes || []).forEach((r) => {
    const line = r.line || (r.geometry && r.geometry.coordinates) || [];
    const shown = routeRenderLayer(r, mfatIds);
    const layer = shown === 'drop' ? 'fat' : shown;
    if (rtByLayer[layer] && line.length >= 2) { rtByLayer[layer].push(line); lenByLayer[layer] += (r.length_m || 0); }
  });
  // round-7 ISSUE-5: render each metre of trench once at its highest layer (the
  // Metro title length above is summed pre-clip, and metro is never clipped, so it
  // is unaffected).
  clipLayersForExport(rtByLayer);

  const styles = [];
  Object.entries(PT_STYLE).forEach(([t, st]) => styles.push(
    `<Style id="pt_${t}"><IconStyle><color>${st.color}</color><scale>${st.scale}</scale><Icon><href>${st.href}</href></Icon></IconStyle></Style>`));
  Object.entries(LINE_KML).forEach(([l, col]) => styles.push(
    `<Style id="ln_${l}"><LineStyle><color>${col}</color><width>${LINE_WIDTH[l] || 3}</width></LineStyle></Style>`));
  Object.entries(AREA_KML).forEach(([t, fillCol]) => {
    const lineCol = AREA_LINE[t] || ('ff' + fillCol.slice(2));
    styles.push(`<Style id="ar_${t}"><PolyStyle><color>${fillCol}</color></PolyStyle><LineStyle><color>${lineCol}</color><width>2</width></LineStyle></Style>`);
  });

  const ptFolder = (title, key) => {
    const pms = elByType[key].map((e) =>
      `<Placemark><name>${kmlEsc(e.name)}</name><styleUrl>#pt_${key}</styleUrl><Point><coordinates>${e.c[0]},${e.c[1]},0</coordinates></Point></Placemark>`).join('');
    return `<Folder><name>${title}</name>${pms}</Folder>`;
  };
  const lineFolder = (title, key) => {
    const pms = rtByLayer[key].map((line, i) =>
      `<Placemark><name>${title}-${i + 1}</name><styleUrl>#ln_${key}</styleUrl><LineString><coordinates>${line.map(([lng, lat]) => `${lng},${lat},0`).join(' ')}</coordinates></LineString></Placemark>`).join('');
    return `<Folder><name>${title}</name>${pms}</Folder>`;
  };
  const metroTitle = `Metro (${Math.round(lenByLayer.metro)}M)`;

  const areaCircleFolder = (title, key) => {
    const pms = elByType[key].map((e) => {
      const [lng, lat] = e.c;
      return `<Placemark><name>${kmlEsc(e.name)}</name><styleUrl>#ar_${key}</styleUrl><Polygon><outerBoundaryIs><LinearRing><coordinates>${circleRing(lng, lat, coverageM)}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>`;
    }).join('');
    return `<Folder><name>${title}</name>${pms}</Folder>`;
  };

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${kmlEsc(city)} - FTK Plan</name>${brand?.name ? `<description>اپراتور: ${kmlEsc(brand.name)}</description>` : ''}
${styles.join('\n')}
<Folder><name>${kmlEsc(city)}</name>
<Folder><name>Point</name>
${ptFolder('OLT', 'olt')}
${ptFolder('ODC', 'odc')}
${ptFolder('MFAT', 'mfat_l2')}
${ptFolder('FAT', 'fat')}
${ptFolder('HH', 'hh')}
${ptFolder('TC', 'tc')}
${ptFolder('HC', 'hc')}
</Folder>
<Folder><name>Line</name>
${lineFolder(metroTitle, 'metro')}
${lineFolder('ODC', 'odc')}
${lineFolder('FAT', 'fat')}
</Folder>
<Folder><name>Area</name>
<Folder><name>POP</name>
${areaCircleFolder('OLT', 'olt')}
${areaCircleFolder('ODC', 'odc')}
</Folder>
</Folder>
</Folder>
</Document></kml>`;
}

export async function downloadDesignKmz(result, city = 'FTTH', brand = {}) {
  const kml = buildDesignKml(result, city, brand);
  const zip = new JSZip();
  zip.file('doc.kml', kml);
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  // the project name can carry filename-illegal chars (e.g. the fa-IR date slashes
  // in the default "طراحی ۱۴۰۵/۳/۱۶"); sanitise for the download name only.
  const safe = String(city).replace(/[\\/:*?"<>|]+/g, '-').trim() || 'FTTH';
  a.href = url; a.download = `${safe}-FTK-Plan.kmz`; a.click();
  URL.revokeObjectURL(url);
}

export function downloadText(filename, text, mime = 'application/vnd.google-earth.kml+xml') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export async function downloadLomLopXlsx(filename, sheets) {
  const headers = await authHeaders(true);
  try {
    const resp = await fetch('/api/design/export/lop-xlsx', {
      method: 'POST', headers, body: JSON.stringify({ filename, sheets }),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      return { error: body.error || 'ساخت فایل اکسل ناموفق بود' };
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    try { a.click(); } finally { URL.revokeObjectURL(url); }
    return { ok: true };
  } catch {
    return { error: 'دانلود فایل اکسل ناموفق بود' };
  }
}

// --- Single-Line Diagram (SLD): schematic tree OLT -> ODC -> FAT ------------
// Not geographic. Built from element parent links (ext_id / type / parent_ext).
function buildSld(result, faN) {
  const sldEsc = (x) => String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const els = result.elements || [];
  const routes = result.routes || [];
  if (els.length === 0) return '<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0"/>';

  // team spec 2026-07-06: 'fat' leaf + 'mfat_l2' level-2 device; legacy mfat/sfat kept.
  const COLORS = { olt: '#dc2626', odc: '#eab308', mfat_l2: '#ec4899', fat: '#a78bfa',
    mfat: '#8b5cf6', sfat: '#a78bfa' };
  const RING_COLOR = '#0f766e';
  const FAT_W = 26;     // box width per FAT
  const GAP = 4;        // gap between FAT boxes
  const SLOT = FAT_W + GAP;
  const Y_OLT = 52; const Y_ODC = 130; const Y_FAT = 240;
  const RING_Y = 18;
  const BOX_H = 22;     // rect height
  const FAT_CAP = 400;  // render at most this many FATs

  const olts = els.filter((e) => e.type === 'olt');

  // Lay out FATs left-to-right, grouped by ODC, grouped by OLT.
  let x = GAP;          // running x cursor along the FAT row
  let fatCount = 0;
  let truncated = false;
  const oltNodes = [];  // {ext_id, cx}
  const odcNodes = [];  // {ext_id, cx, parent_ext}
  const fatNodes = [];  // {ext_id, type, cx, parent_ext}

  outer:
  for (const olt of olts) {
    const odcs = els.filter((e) => e.parent_ext === olt.ext_id && (e.type === 'odc' || e.type === 'mfat_l2'));
    const oltOdcCenters = [];
    for (const odc of odcs) {
      const fats = els.filter((e) => e.parent_ext === odc.ext_id
        && (e.type === 'mfat' || e.type === 'sfat' || e.type === 'fat'));
      const odcFatCenters = [];
      if (fats.length === 0) {
        // ODC with no FATs still occupies one slot so it is visible.
        const cx = x + FAT_W / 2;
        x += SLOT;
        odcFatCenters.push(cx);
      } else {
        for (const fat of fats) {
          if (fatCount >= FAT_CAP) { truncated = true; break outer; }
          const cx = x + FAT_W / 2;
          x += SLOT;
          fatNodes.push({ ext_id: fat.ext_id, type: fat.type, cx, parent_ext: odc.ext_id });
          odcFatCenters.push(cx);
          fatCount += 1;
        }
      }
      const odcCx = odcFatCenters.reduce((a, b) => a + b, 0) / odcFatCenters.length;
      odcNodes.push({ ext_id: odc.ext_id, type: odc.type, cx: odcCx, parent_ext: olt.ext_id });
      oltOdcCenters.push(odcCx);
    }
    let oltCx;
    if (oltOdcCenters.length === 0) {
      // OLT with no ODCs still gets a slot.
      oltCx = x + FAT_W / 2;
      x += SLOT;
    } else {
      oltCx = oltOdcCenters.reduce((a, b) => a + b, 0) / oltOdcCenters.length;
    }
    oltNodes.push({ ext_id: olt.ext_id, cx: oltCx });
  }

  const totalW = Math.max(x + GAP, 200);
  const showEveryFat = fatNodes.length <= 60; // label every FAT only when not cramped
  const parts = [];

  const byExtOlt = {}; oltNodes.forEach((n) => { byExtOlt[n.ext_id] = n; });
  const byExtOdc = {}; odcNodes.forEach((n) => { byExtOdc[n.ext_id] = n; });
  const routeBetween = (fromExt, toExt, layer) => routes.find((r) => {
    if (layer && normalizeLayer(r.layer) !== layer) return false;
    return (r.from_ext === fromExt && r.to_ext === toExt)
      || (r.from_ext === toExt && r.to_ext === fromExt);
  });
  const routeLengthText = (route) => {
    if (!route || route.length_m == null || !Number.isFinite(Number(route.length_m))) return '';
    return `${faN(Math.round(Number(route.length_m)))} متر`;
  };
  const drawEdgeLabel = (label, cx, cy, color = '#334155') => (label
    ? `<text x="${cx.toFixed(1)}" y="${cy.toFixed(1)}" font-size="7" fill="${color}" text-anchor="middle">${sldEsc(label)}</text>`
    : '');

  // The metro routes are the OLT ring. Draw them as dashed arcs above the OLT row.
  routes.filter((r) => normalizeLayer(r.layer) === 'metro').forEach((r) => {
    const from = byExtOlt[r.from_ext];
    const to = byExtOlt[r.to_ext];
    if (!from || !to || from === to) return;
    const midX = (from.cx + to.cx) / 2;
    parts.push(`<path d="M ${from.cx.toFixed(1)} ${Y_OLT} Q ${midX.toFixed(1)} ${RING_Y} ${to.cx.toFixed(1)} ${Y_OLT}" fill="none" stroke="${RING_COLOR}" stroke-width="2.5" stroke-dasharray="6 4"/>`);
    parts.push(drawEdgeLabel(routeLengthText(r), midX, (Y_OLT + RING_Y) / 2 - 3, RING_COLOR));
  });

  // Connector lines run from each child top-centre to its parent bottom-centre.
  odcNodes.forEach((n) => {
    const p = byExtOlt[n.parent_ext];
    if (!p) return;
    const x1 = n.cx; const y1 = Y_ODC; const x2 = p.cx; const y2 = Y_OLT + BOX_H;
    parts.push(`<line x1="${x1.toFixed(1)}" y1="${y1}" x2="${x2.toFixed(1)}" y2="${y2}" stroke="#94a3b8" stroke-width="1"/>`);
    parts.push(drawEdgeLabel(routeLengthText(routeBetween(n.ext_id, p.ext_id)), (x1 + x2) / 2, (y1 + y2) / 2 - 4));
  });
  fatNodes.forEach((n) => {
    const p = byExtOdc[n.parent_ext];
    if (!p) return;
    const x1 = n.cx; const y1 = Y_FAT; const x2 = p.cx; const y2 = Y_ODC + BOX_H;
    parts.push(`<line x1="${x1.toFixed(1)}" y1="${y1}" x2="${x2.toFixed(1)}" y2="${y2}" stroke="#94a3b8" stroke-width="1"/>`);
    parts.push(drawEdgeLabel(routeLengthText(routeBetween(n.ext_id, p.ext_id)), (x1 + x2) / 2, (y1 + y2) / 2 - 4));
  });

  const drawBox = (n, y, color, label, rotate) => {
    const bx = n.cx - FAT_W / 2;
    let txt;
    if (rotate) {
      txt = `<text x="${n.cx.toFixed(1)}" y="${y + BOX_H + 6}" font-size="8" fill="#0f172a" text-anchor="end" transform="rotate(-60 ${n.cx.toFixed(1)} ${y + BOX_H + 6})">${sldEsc(label)}</text>`;
    } else {
      txt = `<text x="${n.cx.toFixed(1)}" y="${y + BOX_H / 2 + 3}" font-size="8" fill="#ffffff" text-anchor="middle">${sldEsc(label)}</text>`;
    }
    return `<rect x="${bx.toFixed(1)}" y="${y}" width="${FAT_W}" height="${BOX_H}" rx="3" fill="${color}" stroke="#475569" stroke-width="0.5"/>${txt}`;
  };

  oltNodes.forEach((n) => parts.push(drawBox(n, Y_OLT, COLORS.olt, n.ext_id, false)));
  odcNodes.forEach((n) => parts.push(drawBox(n, Y_ODC, COLORS[n.type] || COLORS.odc, n.ext_id, false)));
  fatNodes.forEach((n, i) => {
    const color = COLORS[n.type] || COLORS.sfat;
    // when cramped, rotate labels -60deg and only show every Nth label
    if (showEveryFat) {
      parts.push(drawBox(n, Y_FAT, color, n.ext_id, false));
    } else {
      const everyN = Math.ceil(fatNodes.length / 60);
      const label = (i % everyN === 0) ? n.ext_id : '';
      parts.push(drawBox(n, Y_FAT, color, label, true));
    }
  });

  parts.push(`<line x1="8" y1="12" x2="36" y2="12" stroke="${RING_COLOR}" stroke-width="2.5" stroke-dasharray="6 4"/>`);
  parts.push(`<text x="42" y="15" font-size="8" fill="${RING_COLOR}">رینگ مترو</text>`);
  // row legend labels on the far right edge
  parts.push(`<text x="8" y="${Y_OLT - 6}" font-size="9" fill="#64748b">OLT</text>`);
  parts.push(`<text x="8" y="${Y_ODC - 6}" font-size="9" fill="#64748b">ODC</text>`);
  parts.push(`<text x="8" y="${Y_FAT - 6}" font-size="9" fill="#64748b">FAT</text>`);
  if (truncated) parts.push(`<text x="${(totalW / 2).toFixed(0)}" y="295" font-size="10" fill="#dc2626" text-anchor="middle">... (truncated)</text>`);

  // direction="ltr": the schematic is left-to-right; without this the row
  // labels inherit the report's RTL direction and render off the left edge.
  return `<svg xmlns="http://www.w3.org/2000/svg" direction="ltr" width="${Math.ceil(totalW)}" height="300" viewBox="0 0 ${Math.ceil(totalW)} 300">${parts.join('')}</svg>`;
}

// Itemised cost section for the DDR («برآورد هزینه»). Built from summary.costing,
// which the tariff layer freezes onto the design - NOT from bom.total_cost, whose
// six hardcoded engine constants carry no currency and are only a relative
// yardstick. When the design has never been priced we say so plainly instead of
// printing a unitless number next to a ریال heading.
const CURRENCY_LABEL = { IRR: 'ریال', IRT: 'تومان' };

export function costingNotices(costing, carrierId) {
  if (!costing) return [];
  return [
    carrierId && costing.carrier_id !== carrierId ? 'این هزینه با فهرست بهای اپراتور دیگری محاسبه شده است.' : null,
    costing.stale ? 'این هزینه مربوط به نتیجهٔ قبلی طرح است؛ دوباره قیمت‌گذاری کنید.' : null,
  ].filter(Boolean);
}
const pricingAction = (brand) => `قیمت‌گذاری با فهرست بهای ${brand?.name || 'اپراتور فعال'}`;

function buildCostSection(costing, faN, esc, brand) {
  if (!costing || !costing.lines) {
    return `<section class="sec"><div class="sec-h">برآورد هزینه</div>
      <div class="panel" style="color:#334155;line-height:1.9">
        این طرح هنوز با هیچ تعرفه‌ای قیمت‌گذاری نشده است. در پنل طراحی دکمهٔ
        «${esc(pricingAction(brand))}» را بزنید تا صورت هزینه‌ی ریز اینجا درج شود.
      </div></section>`;
  }
  const missingSiteCost = (costing.missing_paths || []).includes('counts.site_billable');
  const unit = CURRENCY_LABEL[costing.currency] || '';
  const rows = costing.lines.map((l) => `<tr>
      <td>${esc(l.name_fa)}</td>
      <td class="mono">${faN(l.qty)}</td>
      <td>${esc(l.unit)}</td>
      <td class="mono">${faN(l.unit_price)}</td>
      <td class="mono"><b>${faN(l.amount)}</b></td>
    </tr>`).join('');

  // Every «توافقی» row that the design actually needs. Listing them by name is
  // what stops the total from being read as the final price.
  const negRows = (costing.negotiable || []).map((l) => `<tr>
      <td>${esc(l.name_fa)}</td>
      <td class="mono">${faN(l.qty)}</td>
      <td>${esc(l.unit)}</td>
      <td colspan="2" style="color:#b91c1c;font-weight:700">توافقی - خارج از جمع</td>
    </tr>`).join('');

  const totalRow = (label, value, strong) => (value
    ? `<div class="bar-row"><span style="flex:1">${label}</span>${strong
      ? `<b style="font-size:15px">${faN(value)}</b>` : `<b>${faN(value)}</b>`}</div>`
    : '');

  return `<section class="sec"><div class="sec-h">برآورد هزینه - ${esc(costing.version_name || '')}</div>
    <div class="panel" style="margin-bottom:12px;color:#334155;line-height:1.9">
      ${costingNotices(costing, brand?.carrierId).map((note) => `<div style="color:#b91c1c;font-weight:700">${esc(note)}</div>`).join('')}
      واحد پول: <b>${unit}</b> · تعداد ردیف: <b>${faN(costing.lines.length)}</b>
      · سرانهٔ هر واحد مسکونی: <b>${faN(costing.cost_per_premise)} ${unit}</b>
      ${missingSiteCost ? '<div style="color:#b91c1c;font-weight:700;margin-top:6px">هزینه‌ی کابینت سایت‌ها محاسبه نشده است؛ طرح را دوباره اجرا کنید.</div>' : ''}
      ${costing.complete ? '' : `<div style="color:#b91c1c;font-weight:700;margin-top:6px">
        هشدار: ${faN((costing.negotiable || []).length)} قلمِ «توافقی» عدد ندارد،
        پس جمع زیر <u>کفِ هزینه</u> است نه هزینه‌ی نهایی.</div>`}
    </div>
    <div class="tbl-wrap"><table>
      <thead><tr><th>شرح کار</th><th>مقدار</th><th>واحد</th><th>بهای واحد (${unit})</th><th>مبلغ (${unit})</th></tr></thead>
      <tbody>${rows}${negRows}</tbody>
    </table></div>
    <div class="panel" style="margin-top:12px">
      ${totalRow('جمع ردیف‌ها', costing.subtotal)}
      ${totalRow('بالاسری', costing.overhead)}
      ${totalRow('پیش‌بینی نشده', costing.contingency)}
      ${totalRow('ارزش افزوده', costing.vat)}
      <div class="bar-row" style="border-top:1px solid #e6eaf0;margin-top:6px;padding-top:8px">
        <span style="flex:1;font-weight:800">جمع کل (${unit})</span>
        <b style="font-size:16px">${faN(costing.total)}</b>
      </div>
    </div>
  </section>`;
}

// Excel-friendly combined List of Materials / List of Prices export. Numeric
// cells deliberately use plain Latin digits so Excel recognises them as numbers;
// Persian is confined to the human-readable label/note columns.
function csvField(value) {
  const text = value == null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// Round 25: a legacy summary has no billable counter, so do not guess its split.
function siteQuantities(summary) {
  const total = summary.counts?.site ?? summary.bom?.site;
  const billable = summary.counts?.site_billable ?? summary.bom?.site_billable;
  return {
    total,
    billable,
    existing: total != null && billable != null ? Number(total) - Number(billable) : null,
  };
}

export function buildLomLopSheets(result, city = 'FTTH', brand) {
  const s = (result && result.summary) || {};
  const bom = s.bom || {};
  const c = s.counts || {};
  const header = ['بخش', 'کد', 'شرح', 'واحد', 'مقدار', 'قیمت واحد', 'قیمت کل', 'توضیح'];
  const lomRows = [header.slice()];
  const lopRows = [header.slice()];
  const latinNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    return Number.isFinite(number) ? String(number) : '';
  };
  const addLom = (code, description, unit, value, note = '') => {
    if (value === null || value === undefined) return;
    lomRows.push(['LOM', code, description, unit, latinNumber(value), '', '', note]);
  };

  // Keep the project name in the same file as its bill, without turning it into
  // a quantity or price cell.
  lomRows.push(['LOM', 'DESIGN', 'نام طرح', '', '', '', '', city]);
  addLom('OLT', 'مرکز OLT', 'عدد', c.olt);
  const sites = siteQuantities(s);
  if (sites.existing != null) {
    addLom('SITE-EXISTING', 'سایت موجود (بدون هزینه‌ی کابینت)', 'عدد', sites.existing);
    addLom('SITE-BILLABLE', 'سایت تازه (با هزینه‌ی کابینت)', 'عدد', sites.billable, 'ردیف کابینت ODC');
  } else {
    addLom('SITE', 'همهٔ سایت‌های OLT (تفکیک هزینه نامشخص)', 'عدد', sites.total, 'برای تفکیک موجود و تازه، طرح را دوباره اجرا کنید');
  }
  addLom('ODC', 'کابینت ODC', 'عدد', c.odc);
  // One row per panel size in use; the engine keys every configured size, zeros included.
  Object.entries(bom.patch_panels || {}).forEach(([cores, count]) => {
    if (Number(count) > 0) {
      addLom(`PATCH-PANEL-${cores}`, `پچ پنل ${Number(cores).toLocaleString('fa-IR')} کر`, 'عدد', count);
    }
  });
  addLom('MFAT', 'دستگاه MFAT', 'عدد', c.mfat);
  addLom('MFAT-DIRECT-SUBSCRIBERS', 'مشترکین مستقیم MFAT (پورت ۱)', 'مشترک', bom.mfat_direct_subscribers);
  // Keep measured drop quantities on saved designs; new designs do not meter them.
  if (dropsMetered(s)) {
    addLom('DROP-CABLE', 'کابل دراپ هوایی ۲ تا ۱۲ کر', 'متر', bom.drop_cable_m);
    addLom('DROP-CABLES', 'تعداد کابل دراپ', 'رشته', bom.drop_cables);
  }
  addLom('FAT', 'جعبه FAT', 'عدد', c.fat);
  addLom('HH', 'حوضچه (HH)', 'عدد', c.hh);
  addLom('TC', 'کانکتور T', 'عدد', c.tc);
  addLom('HC', 'کانکتور H', 'عدد', c.hc);
  // One row per splitter ratio in use (round 23: 1:16 / 1:32 ODC or FAT splitters).
  splitterCounts(bom).forEach(([ratio, count]) => addLom(
    `SPLITTER-1X${ratio}`, `اسپلیتر ۱×${ratio.toLocaleString('fa-IR')}`, 'عدد', ratio === 8 ? bom.splitter_1x8 : count,
  ));
  addLom('CORES-ACTIVE', 'هسته‌های نوری فعال', 'هسته', bom.cores_active);
  addLom('CORES-RESERVE', 'هسته‌های نوری ذخیره', 'هسته', bom.cores_reserve);
  addLom('MFAT-FEED-CORES-ACTIVE', 'هسته‌های تغذیهٔ MFAT (فعال)', 'هسته', bom.mfat_feed_cores_active);
  addLom('MFAT-FEED-CORES-RESERVE', 'هسته‌های تغذیهٔ MFAT (ذخیره)', 'هسته', bom.mfat_feed_cores_reserve);
  addLom('SUBSCRIBER-UNITS', 'مجموع واحدهای مشترک', 'واحد', bom.total_units);
  addLom('SERVING-CAPACITY', 'ظرفیت سرویس‌دهی', 'مشترک', s.serviceable_subscribers);
  addLom('TRENCH-TOTAL', 'طول کل ترانشه', 'متر', s.total_trench_m);
  addLom('TRENCH-PHYSICAL', 'طول فیزیکی ترانشه', 'متر', s.physical_trench_m);

  const layerFa = { metro: 'مترو', odc: 'لایه ODC', fat: 'لایه FAT', drop: 'دراپ' };
  Object.entries(bom.trench_m_by_layer || {}).forEach(([rawLayer, value]) => {
    const layer = normalizeLayer(rawLayer);
    if (layer === 'drop' && !dropsMetered(s)) return;
    addLom(`TRENCH-${String(layer).toUpperCase()}`, `طول ترانشه ${layerFa[layer] || layer}`, 'متر', value);
  });
  // Round 24: cable metres per layer and cable size, parallel cables included
  // (bom.cable_m = { metro: { 144: m }, odc: { 48: m }, fat: { 24: m } }).
  Object.entries(bom.cable_m || {}).forEach(([layer, bySize]) => {
    if (normalizeLayer(layer) === 'drop' && !dropsMetered(s)) return;
    Object.entries(bySize || {}).forEach(([cores, metres]) => {
      if (Number(metres) > 0) {
        addLom(`CABLE-${String(layer).toUpperCase()}-${cores}`,
          `کابل ${layerFa[layer] || layer} ⁧${Number(cores).toLocaleString('fa-IR')}⁩ کر`, 'متر', metres);
      }
    });
  });

  const costing = s.costing;
  if (!costing) {
    lopRows.push(['LOP', '', `این طرح قیمت‌گذاری نشده است - در پنل طراحی دکمهٔ «${pricingAction(brand)}» را بزنید.`, '', '', '', '', '']);
  } else {
    const currency = CURRENCY_FA[costing.currency] || costing.currency || '';
    lopRows.push(['LOP', 'TARIFF-VERSION', 'نسخه تعرفه', '', '', '', '', costing.version_name]);
    costingNotices(costing, brand?.carrierId).forEach((note) => {
      lopRows.push(['LOP', 'COSTING-WARNING', 'هشدار هزینه', '', '', '', '', note]);
    });
    lopRows.push(['LOP', 'EFFECTIVE-FROM', 'تاریخ اجرا', '', '', '', '', costing.effective_from]);
    lopRows.push(['LOP', 'CURRENCY', 'واحد پول', '', '', '', '', currency]);

    (costing.lines || []).forEach((line) => {
      lopRows.push([
        'LOP', line.code, line.name_fa, line.unit, latinNumber(line.qty),
        latinNumber(line.unit_price), latinNumber(line.amount), '',
      ]);
    });
    (costing.negotiable || []).forEach((line) => {
      lopRows.push([
        'LOP', line.code, line.name_fa, line.unit, latinNumber(line.qty), '', '',
        'توافقی - در جمع کل لحاظ نشده',
      ]);
    });

    [
      ['SUBTOTAL', 'جمع ردیف‌ها', costing.subtotal],
      ['OVERHEAD', 'بالاسری', costing.overhead],
      ['CONTINGENCY', 'پیش‌بینی نشده', costing.contingency],
      ['VAT', 'ارزش افزوده', costing.vat],
      ['TOTAL', 'جمع کل', costing.total],
      ['COST-PER-PREMISE', 'هزینه هر واحد مشترک', costing.cost_per_premise],
    ].forEach(([code, description, value]) => {
      lopRows.push(['LOP', code, description, currency, '', '', latinNumber(value), '']);
    });

    if (costing.missing_paths && costing.missing_paths.length) {
      lopRows.push([
        'LOP', 'MISSING-PATHS', 'هشدار منبع مقدار', '', '', '', '',
        `جمع کل کف هزینه است؛ منابع مقدار موجود نیست: ${costing.missing_paths.join('، ')}${costing.missing_paths.includes('counts.site_billable') ? '؛ هزینه‌ی کابینت سایت‌ها محاسبه نشده است' : ''}`,
      ]);
    }
  }

  // Bidi isolation is for HTML presentation, not spreadsheet cell content.
  return [
    { name: 'LOM', rows: lomRows },
    { name: 'LOP', rows: lopRows },
  ].map((sheet) => ({
    ...sheet,
    rows: sheet.rows.map((row) => row.map((cell) => (
      typeof cell === 'string' ? cell.replace(/[\u2066-\u2069]/g, '') : cell
    ))),
  }));
}

export function buildLomLop(result, city = 'FTTH') {
  const rows = buildLomLopSheets(result, city)
    .flatMap((sheet, index) => (index === 0 ? sheet.rows : sheet.rows.slice(1)));
  return `﻿${rows.map((row) => row.map(csvField).join(',')).join('\r\n')}\r\n`;
}

export function buildDesignReport(result, city = 'FTTH', brand = {}) {
  const s = result.summary || {};
  const bom = s.bom || {};
  const c = s.counts || {};
  const v = s.validator || {};
  const ilp = s.ilp || {};
  const pb = s.power_budget || {};
  // team spec 2026-07-06 section 6 outputs: per-OLT PON ports + per-device occupancy.
  const oltPorts = s.olt_ports || [];
  const occ = s.occupancy || {};
  const occCabs = occ.cabinets || [];
  const fatsOcc = occ.fats || null;
  const esc = (x) => String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const faN = (n) => Number(n || 0).toLocaleString('fa-IR');
  const violations = Array.isArray(s.violations)
    ? s.violations
    : (Array.isArray(result.violations) ? result.violations : []);
  const warnings = violations.filter((violation) => violation && violation.severity === 'warning');
  const now = new Date();
  const dateStr = now.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
  const ok = !s.errors;
  // Round 23: splitters, patch panel and FAT cable are per project (capacity_model).
  const cm = capacityModel(s);
  const newModel = cm.max_cables_per_microduct != null;
  const splitterText = splitterCounts(bom).map(([r, n]) => `اسپلیتر ۱×${faN(r)}: ${faN(n)}`).join(' · ');

  const TYPE_FA = {
    olt: 'OLT', odc: 'ODC', mfat_l2: 'MFAT', fat: 'FAT',
    mfat: 'FAT اصلی', sfat: 'FAT فرعی', hh: 'حوضچه',   // retired legacy labels (historical rows)
    tconnector: 'کانکتور T', hconnector: 'کانکتور H',
  };
  const LAYER_FA = { metro: 'مترو', odc: 'لایهٔ ODC', fat: 'لایهٔ FAT', drop: 'دراپ' };

  const kpi = (a, label, value, sub) => `<div class="kpi" style="--a:${a}"><div class="kpi-v">${value}</div><div class="kpi-l">${label}</div>${sub ? `<div class="kpi-s">${sub}</div>` : ''}</div>`;
  const sites = siteQuantities(s);
  const siteKpis = !(sites.total > 0) ? [] : sites.existing != null
    ? [kpi('#16a34a', 'سایت موجود (بدون هزینه‌ی کابینت)', faN(sites.existing)),
      kpi('#d97706', 'سایت تازه (با هزینه‌ی کابینت)', faN(sites.billable), 'ردیف کابینت ODC')]
    : sites.total != null
      ? [kpi('#64748b', 'همهٔ سایت‌های OLT', faN(sites.total), 'تفکیک موجود و تازه نامشخص؛ طرح را دوباره اجرا کنید')]
      : [];
  const kpisTop = [
    kpi('#16a34a', 'مرکز (OLT)', faN(c.olt), 'مبدأ شبکه'),
    kpi('#d97706', 'کابینت (ODC)', faN(c.odc), c.mfat ? `توزیع نوری · ${faN(c.mfat)} MFAT` : 'توزیع نوری'),
    kpi('#2563eb', 'جعبهٔ FAT', faN(c.fat), `یک اسپلیتر ۱×${faN(cm.fat_splitter)} برای ${faN(cm.fat_splitter)} مشترک`),
    kpi('#1e3a8a', 'حوضچه (HH)', faN(c.hh), 'نقطهٔ دسترسی'),
    ...siteKpis,
  ].join('');
  // The headline cost KPIs come from the tariff-priced `costing` when the design
  // has been priced. Before this existed the DDR printed bom.total_cost under the
  // label «واحد پولی» - a unitless engine placeholder presented as money. If the
  // design is unpriced we now say that instead of showing a number nobody can act
  // on. The itemised breakdown lives in buildCostSection below.
  const costing = s.costing;
  const costUnit = costing ? (CURRENCY_LABEL[costing.currency] || '') : '';
  const costNotes = costingNotices(costing, brand?.carrierId);
  const costState = [costing?.stale ? 'قدیمی' : '', brand?.carrierId && costing && costing.carrier_id !== brand.carrierId ? 'اپراتور دیگر' : ''].filter(Boolean).join(' · ');
  const costKpis = costing
    ? [
      kpi('#0d9488', 'هزینه‌ی کل',
        faN(costing.total),
        `${costUnit}${costing.complete ? '' : ' · کفِ هزینه (اقلام توافقی)'}${costState ? ` · ${costState}` : ''}`),
      kpi('#0d9488', 'هزینه‌ی هر واحد', faN(costing.cost_per_premise), `${costUnit} · سرانهٔ پوشش${costState ? ` · ${costState}` : ''}`),
    ]
    : [
      kpi('#94a3b8', 'هزینه‌ی کل', 'قیمت‌گذاری نشده', 'تعرفه‌ای اعمال نشده'),
      kpi('#94a3b8', 'هزینه‌ی هر واحد', '-', 'پس از قیمت‌گذاری'),
    ];
  // Only measured drop metres produce a KPI, including saved legacy designs.
  const dropKpi = dropsMetered(s)
    ? [kpi('#7c3aed', 'کابل دراپ', `${faN(Math.round(bom.drop_cable_m))} <small>متر</small>`,
      bom.drop_cables != null ? `${faN(bom.drop_cables)} کابل · FAT تا ساختمان` : 'FAT تا ساختمان')]
    : [];
  const kpisBot = [
    kpi('#0d9488', 'طول کل ترانشه', `${faN(s.total_trench_m)} <small>متر</small>`, 'مسیر روی خیابان'),
    ...costKpis,
    kpi('#0d9488', 'تعداد واحد', faN(bom.total_units), splitterText),
    ...dropKpi,
  ].join('');
  const costSection = buildCostSection(costing, faN, esc, brand);

  const trEntries = Object.entries(bom.trench_m_by_layer || {})
    .filter(([layer]) => normalizeLayer(layer) !== 'drop' || dropsMetered(s));
  const trMax = Math.max(1, ...trEntries.map(([, val]) => Number(val) || 0));
  const trBars = trEntries.map(([k, val]) => {
    const layer = normalizeLayer(k);
    const color = ROUTE_COLORS[layer] || '#64748b';
    const pct = Math.round((Number(val) / trMax) * 100);
    return `<div class="bar-row"><span class="bar-lab">${esc(LAYER_FA[layer] || layer)}</span><span class="bar-track"><span class="bar-fill" style="width:${pct}%;background:${color}"></span></span><span class="bar-val">${faN(val)} m</span></div>`;
  }).join('') || '<div class="muted">-</div>';

  const ilpText = ilp.available && !ilp.skipped
    ? `هیوریستیک ${faN(ilp.heuristic_fats)} / کفِ دقیق ${faN(ilp.ilp_fats)} - فاصله ${faN(ilp.gap_pct)}٪${ilp.optimal ? '، بهینهٔ اثبات‌شده ✓' : ''}`
    : 'منطقه بزرگ‌تر از حدِ حلِ دقیق است؛ کفِ تئوری ملاک است';
  const valText = v.fat_count != null
    ? `FAT ${faN(v.fat_count)}/${faN(v.fat_min)} (${faN(v.fat_gap_pct)}٪ بالاتر) · ODC ${faN(v.odc_count)}/${faN(v.odc_min)} (${faN(v.odc_gap_pct)}٪)`
    : '-';
  const pbText = pb.checked
    ? `${faN(pb.checked)} مسیر بررسی شد - ${pb.over_budget ? `${faN(pb.over_budget)} مورد فراتر از بودجه` : 'همه در بودجهٔ نوری'} (بیشینه افت ${faN(pb.worst_loss_db)} dB)`
    : 'بررسی نشد';
  const finite = (x) => x != null && Number.isFinite(Number(x));
  const bindingFieldsPresent = [
    v.serving_radius_m, v.mean_fat_per_cabinet, v.fat_capacity_per_cabinet,
  ].every(finite);
  let bindingConstraintText = '';
  if (bindingFieldsPresent && v.binding_constraint === 'radius') {
    bindingConstraintText = `قید فعال: شعاع سرویس‌دهی ${faN(v.serving_radius_m)} متر روی شبکهٔ معابر - میانگین ${faN(v.mean_fat_per_cabinet)} FAT در هر کابینت از ظرفیت ${faN(v.fat_capacity_per_cabinet)}. کاهش تعداد کابینت بدون افزایش شعاع ممکن نیست.`;
  } else if (bindingFieldsPresent && v.binding_constraint === 'capacity') {
    bindingConstraintText = `قید فعال: ظرفیت هر کابینت ${faN(v.fat_capacity_per_cabinet)} FAT است - میانگین ${faN(v.mean_fat_per_cabinet)} FAT در هر کابینت. کاهش تعداد کابینت بدون افزایش ظرفیت ممکن نیست.`;
  }
  const bindingConstraintRow = bindingConstraintText
    ? `<div class="floor-box"><div class="floor-h">عامل محدودکنندهٔ تعداد کابینت</div><div class="cvv">${bindingConstraintText}</div></div>`
    : '';
  const honestFloorRow = [
    v.odc_count, v.odc_min_effective, v.odc_capacity_min_actual,
    v.olt_count, v.olt_min_effective, v.olt_min,
  ].every(finite)
    ? `<div class="floor-box"><div class="floor-h">مقایسهٔ تعداد - فعلی / کفِ مؤثر / کفِ ظرفیتی</div><div class="floor-grid"><div class="floor-item"><span>ODC</span><b>${faN(v.odc_count)} / ${faN(v.odc_min_effective)} / ${faN(v.odc_capacity_min_actual)}</b></div><div class="floor-item"><span>OLT</span><b>${faN(v.olt_count)} / ${faN(v.olt_min_effective)} / ${faN(v.olt_min)}</b></div></div></div>`
    : '';
  const warningBlock = warnings.length
    ? `<div class="warn-box"><div class="warn-h">هشدارهای طراحی</div><ul class="warn-list">${warnings.map((warning) => `<li>${esc(warning.message)}</li>`).join('')}</ul></div>`
    : '';
  const warningChip = warnings.length
    ? `<span class="chip warn">${faN(warnings.length)} هشدار</span>`
    : '';

  const shp = (p, col) => `<svg width="20" height="20" viewBox="0 0 24 24"><path d="${p}" fill="${col}" stroke="#0b1220" stroke-width="1"/></svg>`;
  const lin = (col) => `<svg width="20" height="20" viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12" stroke="${col}" stroke-width="4" stroke-linecap="round"/></svg>`;
  const lgi = (sw, label, sub) => `<div class="lgi">${sw}<div><b>${label}</b>${sub ? `<br><small>${sub}</small>` : ''}</div></div>`;
  const legend = [
    lgi(shp('M 12,2 L 22,12 L 12,22 L 2,12 Z', '#16a34a'), 'مرکز (OLT)', 'نماد ♦ سبز'),
    lgi(shp('M 4,13 L 4,21 L 20,21 L 20,13 L 12,3 Z', '#06b6d4'), 'کابینت (ODC)', 'خانهٔ فیروزه‌ای'),
    lgi(shp('M 12,3 L 21,21 L 3,21 Z', '#2563eb'), 'جعبهٔ FAT', 'مثلث آبی'),
    lgi(shp('M 12,3 L 21,21 L 3,21 Z', '#ec4899'), 'MFAT (هم‌ردهٔ ODC)', 'مثلث صورتی؛ خط تغذیه هم‌رنگ FAT'),
    lgi(shp('M 4,4 L 20,4 L 20,20 L 4,20 Z', '#1e3a8a'), 'حوضچه (HH)', 'مربع سرمه‌ای'),
    lgi(shp('M 12,5 L 18,8.5 L 18,15.5 L 12,19 L 6,15.5 L 6,8.5 Z', '#f59e0b'), 'کانکتور T', 'تکْ‌تپ روی ترانشه'),
    lgi(shp('M 12,5 C 15.9,5 19,8.1 19,12 C 19,15.9 15.9,19 12,19 C 8.1,19 5,15.9 5,12 C 5,8.1 8.1,5 12,5 Z', '#a21caf'), 'کانکتور H', 'هابِ انشعابِ مشترک'),
    lgi(lin('#ef4444'), 'مترو', 'رینگ پشتیبان (OLT/POP)'),
    lgi(lin('#eab308'), 'لایهٔ ODC', 'مترو ← ODC'),
    lgi(lin('#8b5cf6'), 'لایهٔ FAT', 'ODC ← FAT'),
    ...(dropsMetered(s) ? [lgi(lin('#a78bfa'), 'دراپ', 'FAT ← ساختمان')] : []),
    lgi(lin('#64748b'), 'لوز (زیرداکت) میکروداکت', newModel ? 'همهٔ کرهای یک کابل در یک لوز؛ حداکثر ۲ کابل در هر میکروداکت' : 'همهٔ کرهای یک کابل در یک لوز، مترو در داکت جداگانه'),
  ].join('');

  const elTag = (t) => `<span class="tag" style="background:${ELEMENT_COLORS[t] || '#64748b'}">${esc(TYPE_FA[t] || t)}</span>`;
  const rtTag = (l) => `<span class="tag" style="background:${ROUTE_COLORS[l] || '#64748b'}">${esc(LAYER_FA[l] || l)}</span>`;
  // FIX-10: show the clean standard names (and resolve route from/to to them) so
  // the DDR never prints a raw UUID or the internal T#: tile prefix.
  const dn = designDisplayNames(result.elements);
  const dnOf = (ext) => (ext ? (dn[ext] || ext) : '-');
  const elRows = (result.elements || []).map((e) => {
    const p = e.point || (e.geometry && e.geometry.coordinates) || [];
    return `<tr><td class="mono">${esc(dnOf(e.ext_id))}</td><td>${elTag(e.type)}</td><td class="mono">${(p[1] || 0).toFixed(6)}</td><td class="mono">${(p[0] || 0).toFixed(6)}</td><td class="mono muted">${esc(dnOf(e.parent_ext))}</td></tr>`;
  }).join('');
  const rtRows = (result.routes || []).filter((r) => normalizeLayer(r.layer) !== 'drop' || dropsMetered(s)).map((r) => {
    const lay = normalizeLayer(r.layer);
    let from = dnOf(r.from_ext);
    let to = dnOf(r.to_ext);
    // round-11 (team answer 3): the yellow lateral leaves the red metro AT A
    // HANDHOLE and runs to the ODC. The engine's pseudo tap node reuses the ODC
    // ext_id, which printed from=to=the ODC itself - describe it as defined.
    if (lay === 'odc' && (to === '-' || to === from)) { to = from; from = 'رینگ مترو (از هندهول)'; }
    return `<tr><td>${rtTag(lay)}</td><td class="mono">${(r.length_m || 0).toFixed(1)}</td><td class="mono muted">${esc(from)}</td><td class="mono muted">${esc(to)}</td></tr>`;
  }).join('');

  // team spec 2026-07-06 outputs 3/4/5/6/7: capacity, per-OLT PON ports,
  // splitter + core counts, and per-device occupancy. The whole section is
  // conditional so a legacy summary (no olt_ports/occupancy) degrades to nothing.
  const oltBudgets = [...new Set(oltPorts.map((o) => Number(o.budget)).filter(Number.isFinite))];
  const sharedOltBudget = oltBudgets.length === 1 ? oltBudgets[0] : null;
  // Unused OLT ports are legitimate spare capacity (rules.py no longer warns on them);
  // only ports used beyond an OLT's own tier are a defect (olt_ports_over).
  const oltOver = oltPorts.some((o) => Number(o.ports_used) > Number(o.budget));
  const oltSpare = oltPorts.reduce((n, o) => n + Math.max(0, Number(o.budget) - Number(o.ports_used) || 0), 0);
  const portsConfirm = oltPorts.length
    ? (s.olt_ports_all_exact
      ? (sharedOltBudget != null
        ? `همهٔ OLTها دقیقاً ${faN(sharedOltBudget)} پورت مصرف می‌کنند ✓`
        : 'همهٔ OLTها پورت‌هایشان کامل مصرف شده ✓')
      : (oltOver
        ? 'برخی OLTها بیش از بودجهٔ پورتشان مصرف می‌کنند ✕'
        : `${faN(oltSpare)} پورتِ آزاد، ظرفیتِ ذخیرهٔ مجاز`))
    : '';
  const portsTone = s.olt_ports_all_exact ? '#16a34a' : (oltOver ? '#b91c1c' : '#334155');
  // round-11 A4: ODC and MFAT counted in their own columns, never one merged number.
  const devN = (o, t) => (o.devices || []).filter((d) => d.type === t).length;
  const portsRows = oltPorts.map((o) => {
    const exactText = finite(o.budget) ? `دقیقاً ${faN(o.budget)} ✓` : 'کامل ✓';
    const known = finite(o.budget) && finite(o.ports_used);
    const over = known && Number(o.ports_used) > Number(o.budget);
    const status = o.exact ? `<b style="color:#16a34a">${exactText}</b>`
      : over ? `<b style="color:#b91c1c">بیش از ${faN(o.budget)} ✕</b>`
      : `<span style="color:#334155">${known ? `${faN(Number(o.budget) - Number(o.ports_used))} پورتِ آزاد` : 'پورتِ آزاد'}</span>`;
    return `<tr><td class="mono">${esc(dnOf(o.ext_id))}</td><td class="mono">${faN(o.ports_used)} / ${faN(o.budget)}</td><td>${status}</td><td class="mono">${faN(devN(o, 'odc'))}</td><td class="mono">${faN(devN(o, 'mfat_l2'))}</td></tr>`;
  }).join('');
  // Pair the ratio with the capacity the percentage is measured against. Older summaries
  // have no capacity_designed and fall back to the single number they carry.
  const cabOccRows = occCabs.map((cc) => `<tr><td class="mono">${esc(dnOf(cc.ext_id))}</td><td>${elTag(cc.type)}</td><td class="mono">${faN(cc.ports)}</td><td class="mono">${faN(cc.subscribers)} / ${faN(cc.capacity_designed ?? cc.capacity)}</td><td class="mono">${faN(cc.occupancy_pct)}٪</td><td class="mono">${faN(cc.fat_slots_used)} / ${faN(cc.fat_slots)}</td></tr>`).join('');
  const fatOccText = fatsOcc
    ? `${faN(fatsOcc.count)} FAT - میانگین ${faN(fatsOcc.avg_pct)}٪ (کمینه ${faN(fatsOcc.min_pct)}٪ / بیشینه ${faN(fatsOcc.max_pct)}٪)؛ ${faN(fatsOcc.under_half)} موردِ زیرِ نیمه‌ظرفیت`
    : '-';
  const capacityCards = [
    kpi('#2563eb', 'ظرفیتِ سرویس‌دهی', faN(s.serviceable_subscribers), `مشترک (${faN(cm.fat_splitter)} در هر FAT)`),
    kpi('#0891b2', 'دستگاه‌های MFAT', faN(c.mfat), `هم‌ردهٔ ODC، ۲ یا ۴ پورت${cm.mfat_splitter ? ` · اسپلیتر ۱×${faN(cm.mfat_splitter)} روی همهٔ پورت‌ها` : ''}`),
    kpi('#7c3aed', 'هسته‌های نوری', `${faN(bom.cores_active)}<small> فعال</small>`, `ذخیره ${faN(bom.cores_reserve)}`),
    kpi('#0d9488', 'اسپلیترها',
      splitterCounts(bom).map(([r, n]) => `${faN(n)}<small> عدد ۱×${faN(r)}</small>`).join('<br>'),
      `هر FAT: یک ۱×${faN(cm.fat_splitter)} برای ${faN(cm.fat_splitter)} مشترک · هر پورت ODC: یک ۱×${faN(cm.odc_splitter)}${cm.mfat_splitter ? ` · هر پورت MFAT: یک ۱×${faN(cm.mfat_splitter)}` : ''} · ${faN(cm.subscribers_per_port)} مشترک در هر پورت PON`),
  ].join('');
  const capacitySection = (oltPorts.length || occCabs.length)
    ? `<section class="sec"><div class="sec-h">ظرفیت، پورت و اشغال</div>
      <div class="kgrid">${capacityCards}</div>
      <div class="panel" style="margin-top:14px">
        <div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:6px">پورت‌های PON هر OLT ${portsConfirm ? `<span style="font-weight:600;color:${portsTone}">- ${esc(portsConfirm)}</span>` : ''}</div>
        <div class="tbl-wrap"><table><thead><tr><th>OLT</th><th>پورت مصرف‌شده</th><th>وضعیت</th><th>ODC</th><th>MFAT</th></tr></thead><tbody>${portsRows || '<tr><td colspan="5" class="muted">-</td></tr>'}</tbody></table></div>
      </div>
      <div class="panel" style="margin-top:12px">
        <div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:6px">اشغالِ کابینت‌ها <span class="muted" style="font-weight:600">· FAT: ${esc(fatOccText)}</span></div>
        <div class="tbl-wrap"><table><thead><tr><th>کابینت</th><th>نوع</th><th>پورت</th><th>مشترک/ظرفیت</th><th>اشغال</th><th>اسلاتِ FAT</th></tr></thead><tbody>${cabOccRows || '<tr><td colspan="6" class="muted">-</td></tr>'}</tbody></table></div>
      </div>
    </section>`
    : '';

  // FIX-09: acceptance checklist - bind each of the design team's «معیار نهایی
  // پذیرش» rows to a summary field already computed by the engine, so the team
  // auto-verifies compliance instead of re-measuring the KMZ by hand. Every field
  // is null-guarded (state===null -> grey "محاسبه‌نشده" so a legacy design degrades
  // gracefully).
  const cov = s.coverage || {};
  const tx = s.trench_crossings || {};
  const sp = s.shoot_plan || {};
  const cp = s.chain_plan || {};
  const microductCapText = newModel ? 'حداکثر ۲ کابل در هر میکروداکت' : cm.fat_microduct_type === '14x10'
    ? '۱۴/۱۰: هر لوز حداکثر ۹۶ کر'
    : '۸/۵: هر لوز حداکثر ۴۸ کر';
  const fd = s.feeder || {};
  const rp = s.ring_plan || {};
  const ovM = s.layer_overlap_m;
  const parM = s.parallel_trench_m;        // round-7 ISSUE-2 KPI
  const disc = s.disconnected;
  const num = (x) => (x == null ? null : Number(x));
  const chk = (state, label, detail) => {
    const ic = state === true ? '✓' : state === false ? '✕' : '-';
    const cls = state === true ? 'ok' : state === false ? 'bad' : 'na';
    return `<div class="chk ${cls}"><span class="chk-i">${ic}</span><span class="chk-l">${esc(label)}</span><span class="chk-d">${esc(detail)}</span></div>`;
  };
  const checklist = [
    chk(disc == null ? null : num(disc) === 0, 'بدون خط/تجهیزِ رها‌شده (اتصالِ کامل)',
      disc == null ? 'محاسبه‌نشده' : `${faN(disc)} قطعهٔ بدونِ اتصال`),
    chk(ovM == null ? null : num(ovM) <= 5, 'بدونِ همپوشانیِ لایه‌ها',
      ovM == null ? 'محاسبه‌نشده' : `${faN(Math.round(ovM))} متر همپوشانی`),
    chk(parM == null ? null : num(parM) <= Math.max(50, 0.02 * (num(s.physical_trench_m) || 0)),
      'بدونِ موازی‌کاری (حفاریِ دوطرفه)',
      parM == null ? 'محاسبه‌نشده' : `${faN(Math.round(parM))} متر موازی`),
    chk(tx.count == null ? null : num(tx.count) === 0, 'مسیرهای اجرایی (بدونِ عبور از ساختمان)',
      tx.count == null ? 'محاسبه‌نشده' : `${faN(tx.count)} تقاطع (${faN(Math.round(tx.length_m || 0))}m)`),
    chk(cov.over_radius == null ? null : num(cov.over_radius) === 0, 'جانماییِ ODC (پوششِ شعاع)',
      cov.worst_fat_m == null ? 'محاسبه‌نشده' : `بدترین FAT ${faN(Math.round(cov.worst_fat_m))}m / شعاع ${faN(Math.round(cov.radius_m || 0))}m`),
    chk(sp.ok == null ? null : !!sp.ok, 'طولِ شوتینگ مجاز',
      sp.longest_run_m == null ? 'محاسبه‌نشده' : `بلندترین ${faN(Math.round(sp.longest_run_m))}m / حد ${faN(Math.round(sp.max_shoot_m || 0))}m`),
    s.duct_plan ? chk(s.duct_plan.ok, 'حداکثر ۲ کابل در هر میکروداکت (مترو، ODC، FAT)',
      `مترو ${faN(s.duct_plan.metro)} · ODC ${faN(s.duct_plan.odc)} · FAT ${faN(s.duct_plan.fat)} کابل`)
      : chk(cp.longest_chain == null ? null : !violations.some((x) => x && x.code === 'fat_cable_over_capacity'),
        'کابل لایهٔ FAT در لوز (زیرداکت) میکروداکت',
        cp.longest_chain == null ? 'محاسبه‌نشده' : microductCapText),
    chk(fd.offclass_m == null ? null : num(fd.offclass_m) <= 5, 'فیدر روی معابرِ اصلی',
      fd.offclass_m == null ? 'محاسبه‌نشده' : `${faN(Math.round(fd.offclass_m))}m خارج از معبرِ اصلی`),
    chk(rp.dropped_crossing == null ? null : num(rp.dropped_crossing) === 0, 'رینگِ مترو کامل',
      rp.dropped_crossing == null ? 'محاسبه‌نشده' : `${faN(rp.dropped_crossing)} قطعهٔ حذف‌شده`),
    chk(pb.checked ? num(pb.over_budget || 0) === 0 : null, 'بودجهٔ نوری (GPON)',
      pb.checked ? `${faN(pb.over_budget || 0)} مسیر فراتر از بودجه` : 'محاسبه‌نشده'),
  ].join('');

  return `<!doctype html><html dir="rtl" lang="fa"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>گزارش طراحی شبکه (DDR) - ${esc(city)}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800;900&display=swap');
:root{--ink:#0f172a;--muted:#64748b;--line:#e6eaf0;--panel:#f7f9fc;--teal:#0d9488;--teal2:#2dd4bf}
*{box-sizing:border-box}
html{-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:'Vazirmatn',Tahoma,sans-serif;margin:0;background:#e9eef5;color:var(--ink);font-variant-numeric:tabular-nums;line-height:1.65}
.page{max-width:920px;margin:22px auto;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 18px 50px rgba(15,23,42,.14)}
.cover{position:relative;overflow:hidden;background:linear-gradient(135deg,#0b1220 0%,#13243c 58%,#0d9488 230%);color:#e7eef6;padding:34px 40px 28px}
.cover::before{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(148,163,184,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(148,163,184,.08) 1px,transparent 1px);background-size:26px 26px}
.cover::after{content:'';position:absolute;inset-inline-start:-70px;top:-70px;width:250px;height:250px;border-radius:50%;background:radial-gradient(circle,rgba(45,212,191,.22),transparent 70%)}
.cv{position:relative}
.eyebrow{font-size:11.5px;letter-spacing:3px;color:#5eead4;font-weight:800}
.title{font-size:27px;font-weight:900;margin:9px 0 5px;letter-spacing:-.4px;line-height:1.25}
.subtitle{font-size:13px;color:#9fb3c8;max-width:560px}
.meta{display:flex;align-items:center;gap:20px;flex-wrap:wrap;margin-top:18px;font-size:12.5px;color:#cbd5e1}
.meta b{color:#fff;font-weight:700}
.chip{display:inline-flex;align-items:center;gap:7px;padding:6px 13px;border-radius:999px;font-size:12px;font-weight:800}
.chip.ok{background:rgba(34,197,94,.16);color:#86efac;border:1px solid rgba(34,197,94,.42)}
.chip.bad{background:rgba(239,68,68,.16);color:#fca5a5;border:1px solid rgba(239,68,68,.42)}
.chip.warn{background:rgba(245,158,11,.2);color:#fde68a;border:1px solid rgba(245,158,11,.55)}
.body{padding:24px 40px 36px}
.sec{margin-top:30px;animation:rise .5s ease both}
.sec-h{display:flex;align-items:center;gap:10px;font-size:15.5px;font-weight:800;margin-bottom:15px;color:#1e293b}
.sec-h::before{content:'';width:6px;height:19px;border-radius:3px;background:linear-gradient(180deg,var(--teal2),var(--teal))}
.kgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}
.kpi{position:relative;min-width:0;container-type:inline-size;background:var(--panel);border:1px solid var(--line);border-radius:14px;padding:16px}
.kpi::before{content:'';position:absolute;top:0;inset-inline:0;height:4px;border-radius:14px 14px 0 0;background:var(--a,#0d9488)}
.kpi-v{font-size:clamp(18px,10cqi,25px);font-weight:900;line-height:1.1;white-space:nowrap;overflow-wrap:normal}
.kpi-v small{font-size:13px;font-weight:600;color:var(--muted)}
.kpi-l{font-size:12.5px;font-weight:700;color:#334155;margin-top:5px}
.kpi-s{font-size:10.5px;color:var(--muted);margin-top:2px}
.kgrid.k5{display:flex;flex-wrap:wrap}
.kgrid.k5 .kpi{flex:1 1 240px}
.kgrid+.kgrid{margin-top:12px}
.cards{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:15px 16px}
.card .ct{font-size:11.5px;font-weight:800;color:var(--teal);margin-bottom:7px;letter-spacing:.3px}
.card .cvv{font-size:12.5px;color:#334155;line-height:1.8}
.floor-box{margin-top:12px;border:1px solid #cbd5e1;border-radius:12px;padding:12px 14px;background:#f8fafc}
.floor-h{font-size:11.5px;font-weight:800;color:#334155;margin-bottom:8px}
.floor-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}
.floor-item{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 10px;border-radius:9px;background:#fff;color:#0f172a}
.floor-item span{font-weight:900;color:#0f766e}.floor-item b{font-size:13px}
.warn-box{margin-top:12px;border:1px solid #f59e0b;border-radius:12px;padding:12px 15px;background:#fffbeb;color:#78350f}
.warn-h{font-size:13px;font-weight:900;color:#92400e}.warn-list{margin:7px 20px 0 0;padding:0;font-size:12.5px}.warn-list li{margin-top:4px}.warn-list li::marker{color:#d97706}
.split{display:grid;grid-template-columns:1.1fr 1fr;gap:16px}
.panel{background:var(--panel);border:1px solid var(--line);border-radius:14px;padding:16px}
.bar-row{display:flex;align-items:center;gap:10px;margin:9px 0;font-size:12px}
.bar-lab{width:52px;font-weight:700;color:#334155}
.bar-track{flex:1;height:9px;border-radius:6px;background:#e8edf4;overflow:hidden}
.bar-fill{display:block;height:100%;border-radius:6px}
.bar-val{width:78px;text-align:left;color:var(--muted)}
.sld{border:1px solid var(--line);border-radius:14px;padding:14px;background:var(--panel);overflow-x:auto;direction:ltr}
.lgd{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.lgi{display:flex;align-items:center;gap:10px;padding:9px 11px;border:1px solid var(--line);border-radius:11px;background:#fff;font-size:12px}
.lgi b{font-weight:700;color:#334155}
.lgi small{color:var(--muted);font-size:10.5px}
.chk{display:flex;align-items:center;gap:11px;padding:9px 13px;border:1px solid var(--line);border-radius:11px;background:#fff;font-size:12.5px;margin:8px 0}
.chk-i{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:900;color:#fff;flex:none;font-size:13px}
.chk.ok .chk-i{background:#16a34a}.chk.bad .chk-i{background:#dc2626}.chk.na .chk-i{background:#94a3b8}
.chk-l{font-weight:700;color:#334155;flex:1}
.chk-d{color:var(--muted);font-size:11px;text-align:left}
.tbl-wrap{border:1px solid var(--line);border-radius:14px;overflow:hidden}
table{border-collapse:collapse;width:100%;font-size:11.5px}
th{background:#0f172a;color:#cbd5e1;font-weight:700;padding:9px 8px;text-align:center}
td{border-top:1px solid var(--line);padding:7px 8px;text-align:center;color:#334155}
tbody tr:nth-child(even){background:#f7f9fc}
.tag{display:inline-block;min-width:54px;padding:2px 8px;border-radius:6px;font-size:10.5px;font-weight:700;color:#fff}
.mono{color:#475569}
.muted{color:var(--muted)}
.foot{padding:18px 40px;border-top:1px solid var(--line);color:var(--muted);font-size:11px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px}
@keyframes rise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@media print{body{background:#fff}.page{max-width:none;margin:0;box-shadow:none;border-radius:0}.sec{animation:none}.kpi,.card,.tbl-wrap,.sld,.panel,.lgi,.floor-box,.warn-box{break-inside:avoid}@page{size:A4;margin:14mm}}
@media(max-width:680px){.kgrid,.cards,.lgd{grid-template-columns:repeat(2,minmax(0,1fr))}.kgrid+.kgrid{grid-template-columns:1fr}.kgrid.k5{display:grid;grid-template-columns:1fr}.split,.floor-grid{grid-template-columns:1fr}.body,.cover,.foot{padding-left:20px;padding-right:20px}}
</style></head>
<body><div class="page">
  <header class="cover"><div class="cv">
    <div class="eyebrow">FTTH ACCESS NETWORK · سامانهٔ طراحی خودکار</div>
    <div class="title">گزارش طراحی تفصیلی شبکهٔ دسترسی فیبر نوری</div>
    <div class="subtitle">طراحی بهینهٔ توپولوژی و مسیر کابل (OLT / ODC / FAT / حوضچه) ${brand?.name ? `برای اپراتور «${esc(brand.name)}»، ` : ''}با مسیریابی روی شبکهٔ معابر.</div>
    <div class="meta"><span>منطقه: <b>${esc(city)}</b></span><span>تاریخ: <b>${dateStr}</b> ساعت <b>${timeStr}</b></span>
      ${brand?.profileLabel ? `<span>پیش‌تنظیم: <b>${esc(brand.profileLabel)}</b></span>` : ''}${brand?.tariffLabel ? `<span>فهرست بها: <b>${esc(brand.tariffLabel)}</b>${costState ? ` · <b style="color:#b91c1c">${esc(costState)}</b>` : ''}</span>` : ''}
      ${costNotes.map((note) => `<span style="color:#b91c1c;font-weight:700">${esc(note)}</span>`).join('')}
      <span class="chip ${ok ? 'ok' : 'bad'}">${ok ? '✓ منطبق با استاندارد' : `${faN(s.errors)} نقض قانون`}</span>${warningChip}</div>
  </div></header>
  <main class="body">
    <section class="sec"><div class="sec-h">شاخص‌های کلیدی طراحی</div>
      <div class="kgrid">${kpisTop}</div>
      <div class="kgrid${dropKpi.length ? ' k5' : ''}">${kpisBot}</div>
    </section>

    <section class="sec"><div class="sec-h">اعتبارسنجی و بهینگی</div>
      <div class="cards">
        <div class="card"><div class="ct">کفِ تئوریِ تعداد</div><div class="cvv">${valText}</div></div>
        <div class="card"><div class="ct">بهینگیِ دقیق (ILP)</div><div class="cvv">${ilpText}</div></div>
        <div class="card"><div class="ct">بودجهٔ نوری (GPON)</div><div class="cvv">${pbText}</div></div>
      </div>${honestFloorRow}${bindingConstraintRow}${warningBlock}
    </section>

    <section class="sec"><div class="sec-h">معیارهای پذیرشِ طراحی</div>
      ${checklist}
    </section>

    <section class="sec"><div class="sec-h">صورت مصالح و ترانشه</div>
      <div class="split">
        <div class="panel"><div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:6px">طول ترانشه به تفکیک لایه</div>${trBars}</div>
        <div class="panel">
          ${splitterCounts(bom).map(([r, n]) => `<div class="bar-row"><span style="flex:1">اسپلیتر ۱×${faN(r)}</span><b>${faN(n)}</b></div>`).join('')}
          ${bom.cores_active != null ? `<div class="bar-row"><span style="flex:1">هسته‌ها (فعال/ذخیره)</span><b>${faN(bom.cores_active)} / ${faN(bom.cores_reserve)}</b></div>` : ''}
          <div class="bar-row"><span style="flex:1">تعداد واحد</span><b>${faN(bom.total_units)}</b></div></div>
      </div>
    </section>

    ${costSection}

    ${capacitySection}

    <section class="sec"><div class="sec-h">نمودار تک‌خطی (SLD)</div>
      <div class="sld">${buildSld(result, faN)}</div>
    </section>

    <section class="sec"><div class="sec-h">راهنمای نمادها و رنگ‌ها</div>
      <div class="lgd">${legend}</div>
    </section>

    <section class="sec"><div class="sec-h">فهرست عناصر <span class="muted" style="font-weight:600;font-size:12px">(${faN((result.elements || []).length)} مورد)</span></div>
      <div class="tbl-wrap"><table><thead><tr><th>کد</th><th>نوع</th><th>عرض جغرافیایی</th><th>طول جغرافیایی</th><th>والد</th></tr></thead><tbody>${elRows}</tbody></table></div>
    </section>

    <section class="sec"><div class="sec-h">فهرست مسیرها <span class="muted" style="font-weight:600;font-size:12px">(${faN((result.routes || []).length)} مورد)</span></div>
      <div class="tbl-wrap"><table><thead><tr><th>لایه</th><th>طول (متر)</th><th>از</th><th>به</th></tr></thead><tbody>${rtRows}</tbody></table></div>
    </section>
  </main>
  <footer class="foot"><span>سامانهٔ FTTX · گزارش طراحی تفصیلی (DDR)</span><span>تولیدشده در ${dateStr} - ${timeStr}</span></footer>
</div></body></html>`;
}
